
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function InteriorPaintingPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Interior Painting Cape Town | Professional Residential Interior Painters | Premium Finishes';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert interior painting services in Cape Town. Transform your home with professional residential interior painters. Premium finishes, color consultation, and quality workmanship. Free quotes available.');
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'interior painting Cape Town, residential interior painters, home painting services, interior decorating, color consultation');
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/interior-painting`);
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Interior Painting Services Cape Town",
      "description": "Professional interior painting services for residential properties in Cape Town including living rooms, bedrooms, kitchens, and decorative finishes.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/interior-painting`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Cape Town Professional Painters",
        "areaServed": "Cape Town, Western Cape, South Africa"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Interior Painting Services Cape Town')) {
        existingScript.remove();
      }
    };
  }, []);

  const interiorServices = [
    {
      title: 'Living Room Painting',
      description: 'Transform your living space with expert color selection and flawless application techniques.',
      features: ['Feature walls', 'Ceiling treatments', 'Trim and molding', 'Color consultation'],
      icon: 'ri-home-4-line'
    },
    {
      title: 'Bedroom Painting',
      description: 'Create peaceful, personalized spaces with calming colors and professional finishes.',
      features: ['Accent walls', 'Textured finishes', 'Closet interiors', 'Custom color schemes'],
      icon: 'ri-palette-line'
    },
    {
      title: 'Kitchen & Bathroom Painting',
      description: 'Specialized moisture-resistant paints and techniques for high-humidity areas.',
      features: ['Moisture protection', 'Easy-clean finishes', 'Cabinet refinishing', 'Tile preparation'],
      icon: 'ri-brush-3-line'
    },
    {
      title: 'Office & Study Painting',
      description: 'Productive workspace environments with colors that enhance focus and creativity.',
      features: ['Productivity colors', 'Low-odor paints', 'Quick turnaround', 'Minimal disruption'],
      icon: 'ri-check-double-line'
    }
  ];

  const paintFinishes = [
    {
      name: 'Matte Finish',
      description: 'Non-reflective finish that hides imperfections and creates a sophisticated look.',
      bestFor: 'Living rooms, bedrooms, dining rooms'
    },
    {
      name: 'Eggshell Finish',
      description: 'Subtle sheen that\'s easy to clean while maintaining elegance.',
      bestFor: 'Hallways, family rooms, children\'s rooms'
    },
    {
      name: 'Satin Finish',
      description: 'Smooth, velvety finish with gentle luster that\'s highly durable.',
      bestFor: 'Kitchens, bathrooms, high-traffic areas'
    },
    {
      name: 'Semi-Gloss Finish',
      description: 'Reflective finish that\'s extremely durable and easy to clean.',
      bestFor: 'Trim, doors, cabinets, moisture-prone areas'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/7f865a887b0e874815cca9513f5d639d.jpg"
            alt="Professional interior painting services in Cape Town"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Expert <span className="text-yellow-400">Interior Painting</span> Services
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Transform your home with professional interior painting services in Cape Town. Our expert contractors deliver flawless finishes, expert color consultation, and exceptional craftsmanship for every room.
            </p>
            <div className="flex gap-4">
              <Link
                to="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
              <Link
                to="/services"
                className="bg-gray-100 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Interior Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              Comprehensive Interior Painting Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              From single rooms to complete home transformations, our network of professional interior painters delivers exceptional results for every space in your home.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {interiorServices.map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-gray-100">
                {/* Service Icon */}
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                
                <div className="grid grid-cols-2 gap-3">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center">
                      {/* Feature Icon (placeholder, expects feature.icon) */}
                      <i className={`ri-check-double-line text-green-500 mr-2 text-sm`}></i>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Paint Finishes */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Interior Paint Finishes & Options
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose from a variety of premium paint finishes to achieve the perfect look and durability for each room
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintFinishes.map((finish, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow border border-gray-100">
                <h3 className="text-lg font-bold text-black mb-3">{finish.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{finish.description}</p>
                <div className="text-xs text-yellow-600 font-medium">
                  Best for: {finish.bestFor}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Our Interior Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional interior painting that transforms your space with minimal disruption to your daily life
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Color Consultation',
                description: 'Expert advice on color schemes, finishes, and design options that complement your space and lifestyle.'
              },
              {
                step: '02',
                title: 'Surface Preparation',
                description: 'Thorough cleaning, patching, sanding, and priming to ensure perfect paint adhesion and finish.'
              },
              {
                step: '03',
                title: 'Professional Application',
                description: 'Precise painting using premium materials and techniques for flawless, long-lasting results.'
              },
              {
                step: '04',
                title: 'Final Inspection',
                description: 'Complete quality check, touch-ups, and cleanup leaving your home spotless and beautiful.'
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                {/* Process Icon */}
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${index===0?"ri-home-4-line":index===1?"ri-palette-line":index===2?"ri-brush-3-line":"ri-check-double-line"} text-black text-2xl`}></i>
                </div>
                <h4 className="text-xl font-bold text-black mb-4">{process.title}</h4>
                <p className="text-gray-600 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Related Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete your property transformation with our comprehensive painting solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Link
              to="/services/exterior-painting"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-building-2-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Exterior Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Protect and beautify your home's exterior</p>
            </Link>

            <Link
              to="/services/commercial-painting"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-community-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Commercial Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Professional business painting solutions</p>
            </Link>

            <Link
              to="/services/roof-painting"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-home-8-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Roof Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Extend roof life with protective coatings</p>
            </Link>

            <Link
              to="/services/waterproofing"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-droplet-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Waterproofing
              </h3>
              <p className="text-gray-600 text-sm mt-2">Protect against moisture damage</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-r from-yellow-400 to-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">
            Ready to Transform Your Interior?
          </h2>
          <p className="text-xl text-black/80 mb-12 max-w-4xl mx-auto leading-relaxed">
            Connect with Cape Town's most trusted interior painting contractors through our platform. Get up to 5 quotes and choose the perfect contractor for your project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
